import React, { useCallback, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { BillingModal } from './BillingModal';
import {
  BillingMethod,
  CreditBalance,
  Feature,
  OfferType,
  Session,
  useBillingInformationsByOrga,
  useCapabilitiesFirstOffer,
  usePlan,
  useUpdateBillingMethod,
  useUpdatePlan,
} from '@app/api';
import { InformationBox, useToast } from '@app/core';
import { PlanPriceDetails } from '../PriceDetails';
import { useTranslation } from 'react-i18next';
import { useUnlockFeature } from '../Features';
import { PriceTotals } from 'lib/priceComputation';

const useStyles = createUseStyles((theme: Theme) => ({
  infoBox: {
    marginTop: theme.marginBase * 3,
  },
  prices: {
    marginTop: theme.marginBase * 3,
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose: () => any;
  session: Session;
  showAllPriceInfo?: boolean;
  duringSessionProcess?: boolean;
  creditBalance?: CreditBalance;
  priceTotals?: PriceTotals;
}

export const PlanBillingModal = ({
  isOpen,
  onRequestClose,
  session,
  showAllPriceInfo,
  duringSessionProcess,
  creditBalance,
  priceTotals,
}: Props) => {
  const classes = useStyles();
  const planId = session.planId;
  const { t } = useTranslation();
  const toast = useToast();

  const [shouldApplyVat, setShouldApplyVat] = useState<boolean | undefined>(
    undefined,
  );

  const { data: plan } = usePlan(planId);
  const { data: billingInformation } = useBillingInformationsByOrga(
    session.organizationId,
  );
  const { mutateAsync: updatePlan } = useUpdatePlan(planId);
  const { mutateAsync: updateBillingInfo } = useUpdateBillingMethod(
    billingInformation?.id,
  );

  const invoiceOffer = useCapabilitiesFirstOffer(Feature.SEND_INVOICE);
  const unlockSendInvoice = useUnlockFeature(Feature.SEND_INVOICE);

  const onBillingMethodChange = useCallback(
    async (billingMethod: BillingMethod) => {
      try {
        if (invoiceOffer?.type !== OfferType.OPTION) {
          unlockSendInvoice();
          return;
        }
        await updatePlan({ billingMethod });
        if (!billingInformation?.defaultStudyBillingMethod) {
          await updateBillingInfo({ billingMethod });
        }
        toast.saved();
      } catch (e) {
        toast.exception(e);
      }
    },
    [updateBillingInfo, invoiceOffer, planId, toast],
  );

  const specialInformation = useMemo(() => {
    if (!showAllPriceInfo) {
      return undefined;
    }
    const context =
      plan?.billingMethod === BillingMethod.SEND_INVOICE &&
      !billingInformation?.billingInformationValidated
        ? 'notValidated'
        : 'validated';

    return duringSessionProcess
      ? t('billing.paymentMethod.duringSessionProcessInfo', { context })
      : t('billing.pricePane.estimationInfo');
  }, [showAllPriceInfo, duringSessionProcess, plan, billingInformation, t]);

  const updatePaymentMethod = useCallback(
    async ({ paymentMethodId }: { paymentMethodId?: string }) => {
      try {
        if (paymentMethodId) {
          await updatePlan({ paymentMethodId });
          toast.saved();
        }
      } catch (e) {
        toast.exception(e);
      }
      onRequestClose();
    },
    [updatePlan, toast, onRequestClose],
  );

  if (!plan || !billingInformation) {
    return null;
  }

  return (
    <BillingModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      organizationId={session.organizationId}
      onShouldApplyVatChange={setShouldApplyVat}
      defaultBillingMethod={plan.billingMethod || BillingMethod.CREDIT_CARD}
      onBillingMethodChange={onBillingMethodChange}
      specialInformation={specialInformation}
      defaultPaymentMethodId={plan.paymentMethodId}
      onFinish={updatePaymentMethod}
      requireInvoiceFeature
      context="study"
      sessionId={session.id}
      priceInfos={
        plan && (
          <>
            {!showAllPriceInfo && (
              <InformationBox
                text={t('billing.pricePane.estimationInfo')}
                className={classes.infoBox}
                noIcon
              />
            )}
            <PlanPriceDetails
              totalOnly={!showAllPriceInfo}
              className={classes.prices}
              session={session}
              plan={plan}
              applyVat={shouldApplyVat}
              noTooltip={duringSessionProcess}
            />
          </>
        )
      }
      creditBalance={creditBalance}
      priceTotals={priceTotals}
    />
  );
};
